import { type ApproverApiModel } from '@webapp/core/data-access/api-models/ApproverApiModel'
import { type DepartmentApiModel } from '@webapp/core/data-access/api-models/DepartmentApiModel'
import { mapApprovalPool } from '@webapp/core/data-access/mappers/mapApprovalPool'
import { mapApprover } from '@webapp/core/data-access/mappers/mapApprover'
import { mapCurrency } from '@webapp/core/data-access/mappers/mapCurrency'
import { mapDepartment } from '@webapp/core/data-access/mappers/mapDepartment'
import { mapLocation } from '@webapp/core/data-access/mappers/mapLocation'
import { mapRequester } from '@webapp/core/data-access/mappers/mapRequester'
import { type ApprovalPoolConstructorData } from '@webapp/core/entities/internal/ApprovalPool'
import { type ApproverConstructor } from '@webapp/core/entities/internal/Approver'
import { mapOrderApprovalLog } from './mapOrderApprovalLog'
import { mapOrderItemApiModel } from './mapOrderItemApiModel'
import { mapOrderPunchOutSupplier } from './mapOrderPunchOutSupplier'
import { type ApprovalWorkflowConstructorData } from '../../../../core/entities/internal/ApprovalWorkflow'
import { type OrderConstructorData } from '../../entities/Order'
import { type OrderApiModel } from '../api-models/OrderApiModel'

const mapNextApproverGroup = (nextApproverGroup: {
  id?: number
  name: string
  rank: Nullable<number>
}) => {
  if (
    !nextApproverGroup.id ||
    !nextApproverGroup.name ||
    nextApproverGroup.rank === null
  )
    return null
  return {
    id: nextApproverGroup.id,
    name: nextApproverGroup.name,
    rank: nextApproverGroup.rank,
  }
}

const mapNextApproverChoice = (nextApproverChoice: {
  approval_delegatee: Nullable<{
    department: Nullable<DepartmentApiModel>
    email: string
    firstName: string
    id: number
    lastName: string
  }>
  email: string
  firstName: string
  id: number
  lastName: string
  profile_image: string
}): ApproverConstructor => {
  return {
    id: nextApproverChoice.id,
    firstName: nextApproverChoice.firstName,
    lastName: nextApproverChoice.lastName,
    profileImage: nextApproverChoice.profile_image,
    primaryCategory: null,
    secondaryCategory: null,
    approvalDelegatee: nextApproverChoice.approval_delegatee
      ? {
          id: nextApproverChoice.approval_delegatee.id,
          firstName: nextApproverChoice.approval_delegatee.firstName,
          lastName: nextApproverChoice.approval_delegatee.lastName,
          expectedReturnDate: null,
        }
      : null,
  }
}

const mapApprovalWorkflow = (
  approvalWorkflowData: Nullable<{
    current_step: Nullable<{
      group_id: number
      group_name: string
      approver: {
        approvers: ApproverApiModel[]
        group_id: number
        group_name: string
        is_pooled: boolean
        level: number
      }
    }>
    next_step: Nullable<{
      group_id: number
      group_name: string
      options: {
        group_id: number
        group_name: string
        is_pooled: boolean
        level: number
        approvers: ApproverApiModel[]
      }[]
    }>
  }>
): Nullable<ApprovalWorkflowConstructorData> => {
  if (!approvalWorkflowData) {
    return null
  }

  let currentStep: ApprovalWorkflowConstructorData['currentStep']
  let nextStep: ApprovalWorkflowConstructorData['nextStep']

  const currentStepData = approvalWorkflowData.current_step
  const nextStepData = approvalWorkflowData.next_step

  if (currentStepData) {
    const approver: ApproverConstructor | ApprovalPoolConstructorData =
      currentStepData.approver.approvers.length > 1
        ? mapApprovalPool(currentStepData.approver)
        : mapApprover(currentStepData.approver.approvers[0])

    currentStep = {
      groupId: currentStepData.group_id,
      groupName: currentStepData.group_name,
      approver,
    }
  } else {
    currentStep = null
  }

  if (nextStepData) {
    const approverChoices = nextStepData.options.map((option) => {
      return option.approvers.length > 1
        ? mapApprovalPool(option)
        : mapApprover(option.approvers[0])
    })

    nextStep = {
      groupId: nextStepData.group_id,
      groupName: nextStepData.group_name,
      approverChoices,
    }
  } else {
    nextStep = null
  }

  return {
    currentStep,
    nextStep,
  }
}

export const mapOrderApiModel = (
  order: OrderApiModel
): OrderConstructorData => ({
  uuid: order.uuid,
  id: order.num,
  orderNumber: order.order_number ?? order.num,
  status: order.status,
  dateSubmitted: order.date,
  dateRequired: order.dateRequired,
  dateModified: order.dateModified,
  primaryCategory: mapLocation(order.branch),
  secondaryCategory: mapDepartment(order.department),
  requester: mapRequester(order.user),
  approvalDelegateeId: order.approval_delegatee,
  nextApproverGroup: mapNextApproverGroup(order.next_approver_group),
  nextApprover: order.next_approver ? mapApprover(order.next_approver) : null,
  nextApproverChoices: order.next_approver_choices
    ? order.next_approver_choices.map((choice) => {
        return mapNextApproverChoice(choice)
      })
    : null,
  description: order.description,
  currency: mapCurrency(order.currency),
  totalCost: order.totalPrice,
  totalCostInBaseCurrency: order.total_cost_in_base_currency,
  hasBlanketOrderItems: order.has_blanket_order_items,
  punchoutGroup: order.punchout_group,
  punchoutHasRejectedItems: order.punchout_items_rejected,
  punchoutIsRetriable: Boolean(order.punchout_is_retriable),
  punchoutRetriablePurchaseOrderId: order.punchout_order_purchase_order_id,
  punchoutShippingAddress: order.punchout_shipping_address,
  punchoutSupplier: mapOrderPunchOutSupplier(order),
  items: order.order_items.map(mapOrderItemApiModel),
  logs: order.logs.map(mapOrderApprovalLog),
  approvalWorkflow: mapApprovalWorkflow(order.approval_workflow),
  denialReason: order.denial_reason,
  denialComment: order.denial_comment,
})
